import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { fetchOptionsMicroservice } from '../queries';
import Select from './Select';

/**
 * @typedef {Object} SelectWithFetchPathProps
 * @property {'string'} type - the type of the parameter
 * @property {string} value - the value to use for the path
 */

/**
 * @typedef {Object} SelectWithFetchQueryProps
 * @property {'number' | 'string' | 'region'} type - type for the parameter
 * @property {string} key - the key to use for the query
 * @property {string} value - the value to for the query
 * @description Query object for the select with fetch component
 *      Some types may not require a value, eg. region inserts the user's region
 * @example { type: 'number', key: 'amount', value: '1' } => amount=1
 */

/**
 * @typedef {Object} SelectWithFetchPropsEndpoint
 * @property {boolean} direct - whether to use the direct endpoint
 * @property {string} development - endpoint to fetch data from on dev when direct is true
 * @property {string} staging - endpoint to fetch data from on staging when direct is true
 * @property {string} production - endpoint to fetch data from on production when direct is true
 * @property {string} microservice - which microservice to use when direct is false
 * @property {Array<string | SelectWithFetchPathProps>} pathParams -
 *    An array of strings that gets joined with '/' to create the path. If the type
 *    is not a string, it is treated as a query parameter.
 * @property {Array<string | SelectWithFetchQueryProps>} queryParams -
 *    An array of strings that gets joined with '&' to create the query string.
 *    You can also pass in the SelectWithFetchQueryProps object to specify the type
 *    for any custom query parameters.
 */

/**
 *
 * @param {*} props
 * @param {string} props.name - name of the field
 * @param {object} props.query - query object with the following properties:
 * @param {SelectWithFetchPropsEndpoint} props.query.endpoint - endpoint to fetch data from
 * @param {string} props.query.resourceKey - resource key to fetch data from
 * @param {boolean} props.query.useHardcoded - whether to use hardcoded data
 * @param {boolean} props.query.paginatedLoop - whether to use paginated loop
 * @param {string} props.query.labelKey - key to use as the label
 * @returns {React.ReactElement}
 */
const SelectWithFetch = ({ name, query, props, user, filterRef }) => {
    const [options, setOptions] = useState();
    const {
        clearErrors,
        getValues,
        formState: { dirtyFields, errors },
        selectedValues,
        setError,
        setValue,
        updateSelectedValues,
        watch,
    } = useFormContext();
    const { endpoint, resourceKey, useHardcoded, paginatedLoop, labelKey, idKey } = query;

    useEffect(() => {
        const getOptions = async () => {
            // TODO: add error handling
            const optionsResponse = await fetchOptionsMicroservice({ endpoint, resourceKey, useHardcoded, paginatedLoop });
            if (optionsResponse?.error) {
                setError(name, { type: 'manual', message: 'Unable to load data options' });
                setOptions([]);
                return;
            }
            const mappedResponse = optionsResponse?.map((option) => ({ ...option, id: option[idKey] }));
            setOptions(mappedResponse);
        };

        getOptions();
    }, []);

    return (
        <Select
            name={name}
            labelKey={labelKey}
            props={{ ...props, options }}
            loading={!options && !errors[name]}
            // wrap
        />
    );
};

export default SelectWithFetch;
